import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Trash2,
  FileText,
  Plus,
  Minus,
  Pin,
  Pencil,
  Save,
  X,
} from "lucide-react";
import type { TimetableGridEntry } from "./typesV2";
import { DAY_LABELS, DAY_ORDER } from "./typesV2";
import { TimetableGridCSS } from "./TimetableGridCSS";
import { PdfPanel } from "./PdfPanel";
import { EntryEditPopup } from "./EntryEditPopup";
import {
  parseTimeToMinutes,
  normalizeTimeSlot,
  clockRangeKey,
} from "./timeUtils";
import { getEntryWarningLevel, ENTRY_FIELD_COLORS } from "./entryUtils";
import { exclusionHatchStyle, exclusionTitle } from "@/utils/exclusionHatch";
import { spanLabelsFromGrid } from "@/utils/teacherContextGrid";

const UNASSIGNED_DAY = "__UNASSIGNED_DAY__";
const UNASSIGNED_TIME = "__UNASSIGNED_TIME__";

const FIXED_DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT"];
const FIXED_TIME_SLOTS = [
  "7:45-8:30",
  "8:30-9:15",
  "9:20-10:05",
  "10:20-11:05",
  "11:05-11:50",
  "11:50-12:35",
  "12:35-13:20",
  "13:20-14:05",
  "14:05-14:50",
];

function isActiveLesson(e: TimetableGridEntry): boolean {
  return e.is_active !== false && !e.is_cancelled;
}

function clockKeyOf(label: string | null | undefined): string | null {
  return clockRangeKey(label);
}

function pdfAxis(rows: string[]): string[] {
  const axis: string[] = [];
  for (const row of rows) {
    if (/^\d{2}:\d{2}–\d{2}:\d{2}(#\d+)?$/.test(row)) {
      axis.push(row);
      continue;
    }
    const key = clockKeyOf(row);
    if (key) axis.push(key);
  }
  return axis;
}

function activePersonDays(entries: TimetableGridEntry[]): string[] {
  const days = [
    ...new Set(
      entries
        .filter(isActiveLesson)
        .map((e) => e.day_of_week)
        .filter((d): d is string => !!d && d !== UNASSIGNED_DAY)
    ),
  ];
  days.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  return days;
}

function activePersonTimeSlots(entries: TimetableGridEntry[]): string[] {
  const slots = [
    ...new Set(
      entries
        .filter(isActiveLesson)
        .map((e) => normalizeTimeSlot(e.time_slot_label))
        .filter((t): t is string => !!t && t !== UNASSIGNED_TIME)
    ),
  ];
  slots.sort((a, b) => {
    const startA = a.split("-")[0] ?? "";
    const startB = b.split("-")[0] ?? "";
    return parseTimeToMinutes(startA) - parseTimeToMinutes(startB);
  });
  return slots;
}

interface EntriesTabProps<T extends TimetableGridEntry = TimetableGridEntry> {
  entries: T[];
  mode?: "import" | "dashboard";
  onImport?: () => void;
  isImporting?: boolean;
  importResult?: { imported: number; skipped: number };
  totalImported?: number;
  onDeleteEntries?: (indices: number[]) => void;
  onUpdateEntry?: (index: number, updatedEntry: T) => void;
  onAddEntry?: (entry: T) => void;
  onSaveEntry?: (entry: T) => Promise<T>;
  onDeleteEntry?: (entry: T) => Promise<void>;
  onRebindSubject?: (
    entry: T,
    anchor: DOMRect,
    field?: "subject" | "room" | "klass"
  ) => void;
  sessionId?: string | null;
  onLoadEntries?: () => Promise<void>;
  isLoadingEntries?: boolean;
  pendingExtractFileId?: string | null;
  pendingExtractFileType?: string | null;
  fixedTimeSlots?: string[];
  bellSlots?: Array<{
    id: string;
    name?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    slot_number?: number | null;
    is_break?: boolean;
  }>;
  pdfRows?: string[];
  readOnly?: boolean;
  fixedDays?: string[];
}

export function EntriesTab<T extends TimetableGridEntry = TimetableGridEntry>({
  entries,
  mode = "import",
  onImport,
  isImporting,
  importResult,
  totalImported = 0,
  onDeleteEntries,
  onUpdateEntry,
  onAddEntry,
  onSaveEntry,
  onDeleteEntry,
  onRebindSubject,
  sessionId,
  onLoadEntries,
  isLoadingEntries,
  pendingExtractFileId,
  pendingExtractFileType,
  readOnly = false,
  fixedDays,
  bellSlots,
  pdfRows,
}: EntriesTabProps<T>) {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set()
  );
  const [showPdf, setShowPdf] = useState(false);
  const [pdfPage, setPdfPage] = useState(1);
  const [highlightedCell, setHighlightedCell] = useState<{
    day: string;
    timeSlot: string;
  } | null>(null);
  const [highlightedEntryIndices, setHighlightedEntryIndices] = useState<
    Set<number>
  >(new Set());
  const [editPopupTarget, setEditPopupTarget] = useState<{
    entryIndices: number[];
  } | null>(null);
  const [newEntryTarget, setNewEntryTarget] = useState<{
    entry: TimetableGridEntry;
  } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pinned, setPinned] = useState(false);
  const [showIrrelevant, setShowIrrelevant] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<T | null>(null);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const gridSectionRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);

  const entriesWithWarnings = useMemo(
    () => entries.filter((e) => getEntryWarningLevel(e, mode) === "error"),
    [entries, mode]
  );

  const sortedEntries = useMemo(() => {
    return entries
      .map((entry, originalIndex) => ({ entry, originalIndex }))
      .sort((a, b) => {
        const dayA = a.entry.day_of_week ?? UNASSIGNED_DAY;
        const dayB = b.entry.day_of_week ?? UNASSIGNED_DAY;
        if (dayA === UNASSIGNED_DAY && dayB !== UNASSIGNED_DAY) return 1;
        if (dayA !== UNASSIGNED_DAY && dayB === UNASSIGNED_DAY) return -1;
        const dayDiff = DAY_ORDER.indexOf(dayA) - DAY_ORDER.indexOf(dayB);
        if (dayDiff !== 0) return dayDiff;

        return (
          parseTimeToMinutes(normalizeTimeSlot(a.entry.time_slot_label)) -
          parseTimeToMinutes(normalizeTimeSlot(b.entry.time_slot_label))
        );
      });
  }, [entries]);

  const personDays = useMemo(() => activePersonDays(entries), [entries]);
  const personTimeSlots = useMemo(() => {
    const occupied = activePersonTimeSlots(entries);
    if (pdfRows) return pdfAxis(pdfRows);
    if (!bellSlots?.length) return occupied;
    const span = spanLabelsFromGrid(
      entries.filter(isActiveLesson).map((entry) => entry.time_slot_label),
      bellSlots.map((slot) => ({
        id: slot.id,
        name: slot.name,
        slot_number: slot.slot_number ?? null,
        start_time: slot.start_time ?? null,
        end_time: slot.end_time ?? null,
        is_break: slot.is_break,
      }))
    );
    return span.length ? span : occupied;
  }, [entries, bellSlots, pdfRows]);

  const validEntries = entries.length - entriesWithWarnings.length;

  const allSelected =
    entries.length > 0 &&
    sortedEntries.every(({ originalIndex }) =>
      selectedIndices.has(originalIndex)
    );
  const someSelected =
    sortedEntries.some(({ originalIndex }) =>
      selectedIndices.has(originalIndex)
    ) && !allSelected;

  useEffect(() => {
    if (highlightedCell || highlightedEntryIndices.size > 0) {
      const timer = setTimeout(() => {
        setHighlightedCell(null);
        setHighlightedEntryIndices(new Set());
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [highlightedCell, highlightedEntryIndices]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && editingRowIndex !== null) {
        cancelEditing();
      }
      if (e.key === "Enter" && editingRowIndex !== null) {
        const target = e.target as HTMLElement;
        if (
          target.tagName !== "INPUT" &&
          target.tagName !== "SELECT" &&
          target.tagName !== "TEXTAREA"
        ) {
          e.preventDefault();
          saveEditing();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [editingRowIndex]);

  // ResizeObserver nicht mehr benötigt für Split-Screen
  // gridHeight State wird nicht mehr verwendet

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(
        new Set(sortedEntries.map(({ originalIndex }) => originalIndex))
      );
    }
  };

  const toggleSelect = (idx: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleDelete = () => {
    if (selectedIndices.size === 0 || !onDeleteEntries) return;
    const sorted = Array.from(selectedIndices).sort((a, b) => b - a);
    if (editingRowIndex !== null && sorted.includes(editingRowIndex)) {
      cancelEditing();
    }
    onDeleteEntries(sorted);
    setSelectedIndices(new Set());
  };

  const startEditing = useCallback(
    (originalIndex: number) => {
      const entry = entries[originalIndex];
      if (!entry) return;
      setEditingRowIndex(originalIndex);
      setEditDraft({ ...entry });
    },
    [entries]
  );

  const cancelEditing = useCallback(() => {
    setEditingRowIndex(null);
    setEditDraft(null);
  }, []);

  const saveEditing = useCallback(async () => {
    if (editingRowIndex !== null && editDraft) {
      const cleaned = {
        ...editDraft,
        teacher_code: editDraft.teacher_code || null,
        day_of_week: editDraft.day_of_week || null,
        time_slot_label: editDraft.time_slot_label || null,
        subject: editDraft.subject || null,
        room: editDraft.room || null,
        class_name: editDraft.class_name || null,
        lesson_count: editDraft.lesson_count || 1,
        is_multi_period: editDraft.is_multi_period || false,
      };
      if (mode === "dashboard" && onSaveEntry) {
        setSavingIndex(editingRowIndex);
        setSaveError(null);
        try {
          const updated = await onSaveEntry(cleaned as T);
          if (onUpdateEntry) {
            onUpdateEntry(editingRowIndex, updated);
          }
          setEditingRowIndex(null);
          setEditDraft(null);
        } catch (err: any) {
          setSaveError(err?.message || "Fehler beim Speichern");
        } finally {
          setSavingIndex(null);
        }
      } else if (onUpdateEntry) {
        onUpdateEntry(editingRowIndex, cleaned);
        setEditingRowIndex(null);
        setEditDraft(null);
      }
    }
  }, [editingRowIndex, editDraft, onUpdateEntry, onSaveEntry, mode]);

  const clickTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (clickTimerRef.current) window.clearTimeout(clickTimerRef.current);
    },
    []
  );

  const handleGridCellClick = useCallback(
    (
      _day: string,
      _timeSlot: string,
      entryIndices: number[],
      event?: { currentTarget: EventTarget },
      field?: "subject" | "room" | "klass"
    ) => {
      setHighlightedEntryIndices(new Set(entryIndices));
      setHighlightedCell({ day: _day, timeSlot: _timeSlot });

      const firstIndex = entryIndices[0];
      const rowElement = listContainerRef.current?.querySelector(
        `[data-entry-index="${firstIndex}"]`
      );
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      if (!readOnly && onRebindSubject && firstIndex != null) {
        const entry = entries[firstIndex];
        if (!entry) return;
        const el = event?.currentTarget as HTMLElement | undefined;
        const anchor =
          el?.getBoundingClientRect() ?? new DOMRect(24, 120, 80, 40);
        if (clickTimerRef.current) window.clearTimeout(clickTimerRef.current);
        clickTimerRef.current = window.setTimeout(() => {
          onRebindSubject(entry, anchor, field);
        }, 220);
      }
    },
    [entries, onRebindSubject, readOnly]
  );

  const handleGridCellDoubleClick = useCallback(
    (_day: string, _timeSlot: string, entryIndices: number[]) => {
      if (readOnly) return;
      if (clickTimerRef.current) window.clearTimeout(clickTimerRef.current);
      setEditPopupTarget({ entryIndices });
    },
    [readOnly]
  );

  const handleEmptyCellClick = useCallback(
    (_day: string, _timeSlot: string) => {
      if (readOnly) return;
      const newEntry: TimetableGridEntry = {
        id: undefined,
        teacher_code: null,
        day_of_week: _day === UNASSIGNED_DAY ? null : _day,
        time_slot_label: _timeSlot === UNASSIGNED_TIME ? null : _timeSlot,
        lesson_number: null,
        subject: null,
        room: null,
        class_name: null,
        is_multi_period: false,
        lesson_count: 1,
        raw_content: "",
      };
      setNewEntryTarget({ entry: newEntry });
    },
    [readOnly]
  );

  const handleSaveNewEntry = useCallback(
    async (entry: TimetableGridEntry) => {
      if (mode === "dashboard" && onSaveEntry) {
        try {
          const updated = await onSaveEntry(entry as T);
          if (onAddEntry) {
            onAddEntry(updated);
          }
        } catch (err: any) {
          setSaveError(err?.message || "Fehler beim Erstellen");
        }
      } else if (onAddEntry) {
        onAddEntry(entry as T);
      }
      setNewEntryTarget(null);
    },
    [onAddEntry, onSaveEntry, mode]
  );

  const handleAddNewEntry = useCallback(() => {
    const newEntry: TimetableGridEntry = {
      id: undefined,
      teacher_code: null,
      day_of_week: null,
      time_slot_label: null,
      lesson_number: null,
      subject: null,
      room: null,
      class_name: null,
      is_multi_period: false,
      lesson_count: 1,
      raw_content: "",
    };
    setNewEntryTarget({ entry: newEntry });
  }, []);

  const handleListRowClick = useCallback(
    (entryIndex: number) => {
      if (editingRowIndex === entryIndex) return;
      const entry = entries[entryIndex];
      if (!entry) return;

      const day = entry.day_of_week ?? UNASSIGNED_DAY;
      const normalizedTime =
        normalizeTimeSlot(entry.time_slot_label) ??
        entry.time_slot_label ??
        UNASSIGNED_TIME;
      setHighlightedCell({ day, timeSlot: normalizedTime });
      setHighlightedEntryIndices(new Set([entryIndex]));

      const gridEntryElement = document.querySelector(
        `[data-grid-entry-index="${entryIndex}"]`
      );
      if (gridEntryElement) {
        gridEntryElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    },
    [entries, editingRowIndex]
  );

  const handleListRowDoubleClick = useCallback((idx: number) => {
    setEditPopupTarget({ entryIndices: [idx] });
  }, []);

  const handleSaveEdit = useCallback(
    (index: number, updatedEntry: TimetableGridEntry) => {
      if (onUpdateEntry) {
        onUpdateEntry(index, updatedEntry as T);
      }
    },
    [onUpdateEntry]
  );

  const handleDeleteFromPopup = useCallback(
    async (index: number) => {
      const entry = entries[index];
      if (mode === "dashboard" && onDeleteEntry && entry) {
        try {
          await onDeleteEntry(entry);
        } catch (err: any) {
          setSaveError(err?.message || "Fehler beim Löschen");
        }
      } else if (onDeleteEntries) {
        onDeleteEntries([index]);
      }
    },
    [onDeleteEntries, onDeleteEntry, entries, mode]
  );

  if (entries.length === 0 && !importResult && isLoadingEntries) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Einträge</h2>
        </div>
        <div className="py-12 text-center text-sm text-gray-500">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-gray-300" />
          Laden...
        </div>
      </div>
    );
  }

  if (entries.length === 0 && !importResult) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Einträge</h2>
        </div>
        <div className="py-12 text-center">
          <ClipboardList className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Keine Einträge vorhanden
          </h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
            {mode === "dashboard"
              ? readOnly
                ? "Kein Stundenplan vorhanden."
                : "Kein Stundenplan vorhanden. Bitte warten Sie, bis der Stundenplan importiert wurde."
              : "Extrahiere zuerst eine PDF und ordne die Token zu, um Einträge zu erstellen."}
          </p>
          {mode === "import" && sessionId && onLoadEntries && (
            <button
              onClick={onLoadEntries}
              disabled={isLoadingEntries}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isLoadingEntries ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Laden...
                </>
              ) : (
                <>
                  <ClipboardList className="w-4 h-4" />
                  Einträge vom Server laden
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0">
      {mode !== "dashboard" && (
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Einträge</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {entries.length} Einträge · {validEntries} gültig ·{" "}
              {entriesWithWarnings.length} mit Warnungen
            </p>
          </div>
          <div className="flex items-center gap-2">
            {mode === "import" &&
              pendingExtractFileId &&
              pendingExtractFileType === "pdf" && (
                <button
                  onClick={() => setShowPdf((p) => !p)}
                  className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                    showPdf
                      ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  {showPdf ? "PDF schliessen" : "PDF anzeigen"}
                </button>
              )}
            {onAddEntry && !readOnly && (
              <button
                onClick={handleAddNewEntry}
                className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Entry hinzufügen
              </button>
            )}
            {mode === "import" &&
              onDeleteEntries &&
              selectedIndices.size > 0 && (
                <button
                  onClick={handleDelete}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Löschen ({selectedIndices.size})
                </button>
              )}
            {mode === "import" && onImport && (
              <button
                onClick={onImport}
                disabled={isImporting || entries.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importieren...
                  </>
                ) : totalImported > 0 ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Erneut importieren ({totalImported} importiert)
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    {entries.length > 0
                      ? `${entries.length} Einträge importieren`
                      : "Importieren"}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Warnings - only in import mode */}
      {mode === "import" && entriesWithWarnings.length > 0 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded mx-4 mt-3 flex items-start gap-2 shrink-0">
          <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-yellow-700">
            {entriesWithWarnings.length} Einträge haben fehlende Felder
            (Lehrer-Code, Wochentag oder Zeit). Diese werden beim Import
            übersprungen.
          </div>
        </div>
      )}

      {/* Save Error */}
      {saveError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded mx-4 mt-3 flex items-start gap-2 shrink-0">
          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-700">{saveError}</div>
        </div>
      )}

      {/* Import Result - only in import mode */}
      {mode === "import" && importResult && (
        <div className="p-3 bg-green-50 border border-green-200 rounded mx-4 mt-3 flex items-center gap-2 shrink-0">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <span className="text-sm text-green-700">
            Import abgeschlossen: {importResult.imported} Einträge importiert,{" "}
            {importResult.skipped} übersprungen.
          </span>
        </div>
      )}

      {/* Main Content */}
      {mode === "dashboard" ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div
            ref={gridSectionRef}
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          >
            <div className="h-full min-h-0 min-w-0 w-full flex-1 overflow-hidden">
              <TimetableGridCSS
                entries={entries}
                onCellClick={handleGridCellClick}
                onCellDoubleClick={handleGridCellDoubleClick}
                onEmptyCellClick={handleEmptyCellClick}
                highlightedCell={highlightedCell}
                highlightedEntryIndices={highlightedEntryIndices}
                fitToView={false}
                zoom={zoom}
                fixedDays={fixedDays ?? personDays}
                fixedTimeSlots={personTimeSlots}
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`flex flex-1 overflow-hidden ${showPdf ? "flex-row" : "flex-col"}`}
        >
          {/* Left: Grid + List */}
          <div
            className={`flex flex-col overflow-hidden ${showPdf ? "w-2/3" : "w-full"}`}
          >
            {/* Grid Section */}
            <div
              ref={gridSectionRef}
              className={`border-b ${pinned ? "flex-1 overflow-hidden flex flex-col min-h-0" : "flex-shrink-0"}`}
            >
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                <span className="text-xs font-medium text-gray-600">
                  Stundenplan
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setZoom((z) => Math.max(0.3, z - 0.1))}
                    className="px-1.5 py-1 rounded text-xs bg-gray-100 hover:bg-gray-200 flex items-center"
                    title="Verkleinern"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => setZoom(1)}
                    className="px-2 py-1 rounded text-xs bg-gray-100 hover:bg-gray-200 min-w-[48px]"
                    title="Zoom zurücksetzen"
                  >
                    {Math.round(zoom * 100)}%
                  </button>
                  <button
                    onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
                    className="px-1.5 py-1 rounded text-xs bg-gray-100 hover:bg-gray-200 flex items-center"
                    title="Vergrössern"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <div className="w-px h-4 bg-gray-300 mx-1" />
                  <button
                    onClick={() => setPinned((p) => !p)}
                    className={`px-1.5 py-1 rounded text-xs flex items-center ${
                      pinned
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                    title={
                      pinned
                        ? "Split-Screen deaktivieren"
                        : "Split-Screen aktivieren"
                    }
                  >
                    <Pin className="w-3 h-3" />
                  </button>
                  {mode === "import" && (
                    <>
                      <div className="w-px h-4 bg-gray-300 mx-1" />
                      <label className="flex items-center gap-1 text-xs cursor-pointer px-1">
                        <input
                          type="checkbox"
                          checked={showIrrelevant}
                          onChange={() => setShowIrrelevant((s) => !s)}
                          className="rounded"
                        />
                        Irrelevante anzeigen
                      </label>
                    </>
                  )}
                </div>
              </div>
              <div
                style={{
                  maxHeight: pinned ? "100%" : "70vh",
                  height: pinned ? "100%" : undefined,
                }}
                className="overflow-auto w-full"
              >
                <TimetableGridCSS
                  entries={entries}
                  onCellClick={handleGridCellClick}
                  onCellDoubleClick={handleGridCellDoubleClick}
                  onEmptyCellClick={handleEmptyCellClick}
                  highlightedCell={highlightedCell}
                  highlightedEntryIndices={highlightedEntryIndices}
                  fitToView={false}
                  zoom={zoom}
                  fixedDays={showIrrelevant ? FIXED_DAYS : undefined}
                  fixedTimeSlots={showIrrelevant ? FIXED_TIME_SLOTS : undefined}
                />
              </div>
            </div>
            <div
              className={`shrink-0 ${pinned ? "border-t-2 border-blue-200" : "border-t"}`}
            />

            {/* List Section */}
            <div
              ref={listContainerRef}
              className="flex-1 overflow-auto min-h-0"
            >
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    {mode === "import" && (
                      <th className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected;
                          }}
                          onChange={toggleSelectAll}
                        />
                      </th>
                    )}
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      #
                    </th>
                    {mode === "import" && (
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        Lehrer-Code
                      </th>
                    )}
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      Wochentag
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      Zeit
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      Lektion
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      Fach
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      Raum
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      Klasse
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      Aktion
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map(({ entry, originalIndex }, displayIdx) => {
                    const isCancelled = entry.is_cancelled;
                    if (editingRowIndex === originalIndex && editDraft) {
                      return (
                        <tr
                          key={originalIndex}
                          data-entry-index={originalIndex}
                          className="bg-blue-50 border-b"
                        >
                          {mode === "import" && (
                            <td className="px-3 py-2 text-center"></td>
                          )}
                          <td className="px-3 py-2 text-gray-500">
                            {displayIdx + 1}
                          </td>
                          {mode === "import" && (
                            <td className="px-3 py-2">
                              <input
                                value={editDraft.teacher_code || ""}
                                onChange={(e) =>
                                  setEditDraft((d) =>
                                    d
                                      ? { ...d, teacher_code: e.target.value }
                                      : null
                                  )
                                }
                                className="w-full px-2 py-1 border rounded text-xs font-mono"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                          )}
                          <td className="px-3 py-2">
                            <select
                              value={editDraft.day_of_week || ""}
                              onChange={(e) =>
                                setEditDraft((d) =>
                                  d
                                    ? {
                                        ...d,
                                        day_of_week: e.target.value || null,
                                      }
                                    : null
                                )
                              }
                              className="w-full px-2 py-1 border rounded text-xs"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="">—</option>
                              {Object.entries(DAY_LABELS)
                                .filter(([k]) => !k.startsWith("__"))
                                .map(([key, label]) => (
                                  <option key={key} value={key}>
                                    {label}
                                  </option>
                                ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={editDraft.time_slot_label || ""}
                              onChange={(e) =>
                                setEditDraft((d) =>
                                  d
                                    ? { ...d, time_slot_label: e.target.value }
                                    : null
                                )
                              }
                              placeholder="7:45-8:30"
                              className="w-full px-2 py-1 border rounded text-xs"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={editDraft.lesson_number ?? ""}
                              onChange={(e) =>
                                setEditDraft((d) =>
                                  d
                                    ? {
                                        ...d,
                                        lesson_number: e.target.value
                                          ? parseInt(e.target.value)
                                          : null,
                                      }
                                    : null
                                )
                              }
                              className="w-full px-2 py-1 border rounded text-xs"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={editDraft.subject || ""}
                              onChange={(e) =>
                                setEditDraft((d) =>
                                  d ? { ...d, subject: e.target.value } : null
                                )
                              }
                              className="w-full px-2 py-1 border rounded text-xs"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={editDraft.room || ""}
                              onChange={(e) =>
                                setEditDraft((d) =>
                                  d ? { ...d, room: e.target.value } : null
                                )
                              }
                              className="w-full px-2 py-1 border rounded text-xs"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={editDraft.class_name || ""}
                              onChange={(e) =>
                                setEditDraft((d) =>
                                  d
                                    ? { ...d, class_name: e.target.value }
                                    : null
                                )
                              }
                              className="w-full px-2 py-1 border rounded text-xs"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveEditing();
                                }}
                                disabled={savingIndex === originalIndex}
                                className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                                title="Speichern"
                              >
                                {savingIndex === originalIndex ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Save className="w-3.5 h-3.5" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cancelEditing();
                                }}
                                className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                                title="Abbrechen"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const warningLevel = getEntryWarningLevel(entry, mode);
                    const isSelected = selectedIndices.has(originalIndex);
                    const isHighlighted =
                      highlightedEntryIndices.has(originalIndex);
                    return (
                      <tr
                        key={originalIndex}
                        data-entry-index={originalIndex}
                        className={`border-b cursor-pointer transition-colors ${
                          isCancelled
                            ? "opacity-50 line-through bg-gray-100"
                            : isHighlighted && isSelected
                              ? "bg-blue-200 ring-2 ring-blue-500"
                              : isHighlighted
                                ? "bg-blue-100 ring-1 ring-blue-500"
                                : isSelected
                                  ? "bg-blue-50 ring-1 ring-blue-300"
                                  : warningLevel === "error"
                                    ? "bg-red-50"
                                    : warningLevel === "warning"
                                      ? "bg-yellow-50"
                                      : ""
                        }`}
                        onClick={() => handleListRowClick(originalIndex)}
                        onDoubleClick={() =>
                          handleListRowDoubleClick(originalIndex)
                        }
                      >
                        {mode === "import" && (
                          <td
                            className="px-3 py-2 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="rounded"
                              checked={isSelected}
                              onChange={() => toggleSelect(originalIndex)}
                            />
                          </td>
                        )}
                        <td className="px-3 py-2 text-gray-500">
                          {displayIdx + 1}
                        </td>
                        {mode === "import" && (
                          <td className="px-3 py-2">
                            {entry.teacher_code ? (
                              <span
                                className={`font-mono px-1 py-0.5 rounded text-xs ${isCancelled ? "bg-gray-200 text-gray-500" : `${ENTRY_FIELD_COLORS.teacher_code.bg} ${ENTRY_FIELD_COLORS.teacher_code.text}`}`}
                              >
                                {entry.teacher_code}
                              </span>
                            ) : (
                              <span className="text-red-500 text-xs">—</span>
                            )}
                          </td>
                        )}
                        <td
                          className={`px-3 py-2 ${isCancelled ? "text-gray-500" : "text-gray-700"}`}
                        >
                          {entry.day_of_week
                            ? DAY_LABELS[entry.day_of_week] || entry.day_of_week
                            : "?"}
                        </td>
                        <td
                          className={`px-3 py-2 ${isCancelled ? "text-gray-500" : "text-gray-700"}`}
                        >
                          {normalizeTimeSlot(entry.time_slot_label) ||
                            entry.time_slot_label ||
                            "—"}
                        </td>
                        <td
                          className={`px-3 py-2 ${isCancelled ? "text-gray-500" : "text-gray-700"}`}
                        >
                          {entry.lesson_number ?? "—"}
                        </td>
                        <td className="px-3 py-2">
                          {entry.subject ? (
                            <span
                              className={`px-1 py-0.5 rounded text-xs ${isCancelled ? "bg-gray-200 text-gray-500" : `${ENTRY_FIELD_COLORS.subject.bg} ${ENTRY_FIELD_COLORS.subject.text}`}`}
                              style={
                                entry.substitution_excluded
                                  ? exclusionHatchStyle
                                  : undefined
                              }
                              title={
                                entry.substitution_excluded
                                  ? exclusionTitle
                                  : undefined
                              }
                            >
                              {entry.subject}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {entry.room ? (
                            <span
                              className={`px-1 py-0.5 rounded text-xs ${isCancelled ? "bg-gray-200 text-gray-500" : `${ENTRY_FIELD_COLORS.room.bg} ${ENTRY_FIELD_COLORS.room.text}`}`}
                            >
                              {entry.room}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {entry.class_name ? (
                            <span
                              className={`px-1 py-0.5 rounded text-xs ${isCancelled ? "bg-gray-200 text-gray-500" : `${ENTRY_FIELD_COLORS.class_name.bg} ${ENTRY_FIELD_COLORS.class_name.text}`}`}
                            >
                              {entry.class_name}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {readOnly ? null : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditing(originalIndex);
                              }}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <Pencil className="w-3 h-3" />
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: PDF Panel - only in import mode */}
          {mode === "import" && showPdf && (
            <div className="w-1/3 border-l overflow-hidden">
              <PdfPanel
                fileId={pendingExtractFileId || null}
                currentPage={pdfPage}
                onPageChange={setPdfPage}
              />
            </div>
          )}
        </div>
      )}

      {/* Edit Popup */}
      {editPopupTarget && (
        <EntryEditPopup
          entries={entries}
          entryIndices={editPopupTarget.entryIndices}
          mode={mode}
          onSave={handleSaveEdit}
          onDelete={handleDeleteFromPopup}
          onClose={() => setEditPopupTarget(null)}
        />
      )}

      {/* New Entry Popup */}
      {newEntryTarget && (
        <EntryEditPopup
          newEntry={newEntryTarget.entry}
          mode={mode}
          onSaveNew={handleSaveNewEntry}
          onClose={() => setNewEntryTarget(null)}
        />
      )}
    </div>
  );
}
